import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { combineLatest, forkJoin, takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
import { FileObject } from '../../../../core/interfaces';
import {
  AI_TOKENS_LOCKED_MESSAGE,
  FUTURE_MODE_LABELS,
  NewReferralForm,
  PatientReferral,
  PatientReferralRecipient,
  REFERRAL_REASON_MAX,
  REFERRAL_SUMMARY_FILES_MAX,
  REFERRAL_SUMMARY_INSTRUCTION_MAX,
  REFERRAL_SUMMARY_MAX,
  ReferralAcknowledgement,
  ReferralFutureMode,
  ReferralRecipientOption,
  ReferralShareMode,
  ReferralUrgency,
  ShareableFile,
  SummaryAiGate,
  SummaryDraft,
  URGENCY_LABELS,
  UnsharedEntry,
  allowedShareModes,
  applySummaryDraft,
  applySummaryStream,
  canStopSharing,
  canWidenToFull,
  describeReferralFailure,
  referralFieldNames,
  shareableGalleryFiles,
  letterOnly,
  newReferralProblems,
  recipientShareSummary,
  recipientStatusLabel,
  summaryAiGate,
  summaryDraftNote,
  unsharedEntryLine,
} from '../../../../core/models/referral.model';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { GroupService } from '../../../../core/services/group.service';
import { ChatService } from '../../../../chat/chat.service';
import { ReferralService } from '../../../../core/services/referral.service';
import { SocketService } from '../../../../core/services/socket.service';
import { TokenUsageService } from '../../../../core/services/token-usage.service';
import { ReferralSummaryFilePickerComponent, ReferralSummaryFilePickerData } from './referral-summary-file-picker/referral-summary-file-picker.component';

export interface PatientReferralDialogData {
  patientId: string;
  patientName: string;
  /** Admin or Physician: may refer and stop sharing. The server enforces the same rule. */
  canRefer: boolean;
  /** §19 P8: opens straight onto a share-back to the practice that referred this patient (a summary chosen when accepting). */
  shareBack?: { linkId: string; replyToReceiptId: string; shareMode: 'full' | 'summary'; practiceName: string };
  /**
   * Network chat (2026-09-22): opens straight onto a new referral to this link (a patient linked in a chat with someone
   * the patient is not referred to yet). What may be sent follows the recipient as usual: a referral note for a
   * clinician outside MedicAI, the file or a summary for a MedicAI practice.
   */
  preselectLinkId?: string;
}

export interface PatientReferralDialogResult {
  changed?: boolean;
  /** the network links a referral was sent to while the dialog was open */
  sentLinkIds?: string[];
}

/**
 * Refer a patient to external clinicians, and see what has been shared with whom (tasks/referrals.md §5.3).
 *
 * Opened from the patient file's toolbar. Two halves: this patient's referrals, per recipient, with "stop sharing";
 * and a new referral. The acknowledgement is shown in the server's own words and version, and nothing can be sent
 * without ticking it (D1).
 *
 * What is shared (architecture §15): the whole timeline, or a summary. For a summary the physician generates an AI draft
 * (it uses the practice's AI tokens and is not stored), streamed in as the discharge summary is, then edits it or tells
 * MedicAI how to change it. Sending it is their confirmation that they reviewed it. A summary is point in time, so the
 * future-sharing choice is hidden. They may also write the summary without generating one.
 *
 * AI tokens (§16, T1): drafting is locked the way Cura is (below zero, never for a premium practice), live as the balance
 * changes; writing by hand stays open.
 */
@Component({
  selector: 'app-patient-referral-dialog',
  templateUrl: './patient-referral-dialog.component.html',
  styleUrls: ['./patient-referral-dialog.component.scss'],
})
export class PatientReferralDialogComponent extends BaseComponent implements OnInit {
  referrals: PatientReferral[] = [];
  options: ReferralRecipientOption[] = [];
  acknowledgement: ReferralAcknowledgement | null = null;
  isLoading = true;
  loadError: string | null = null;
  isSubmitting = false;
  submitError: string | null = null;
  submitAttempted = false;
  stoppingRecipientId: string | null = null;
  /** Links this practice has that this server cannot send to: an empty list is then not an empty network. */
  switchedOffLinks = 0;
  wideningRecipientId: string | null = null;
  /** M6: chat is on for this platform (the hub answered) */
  chatEnabled = false;
  openingChatId: string | null = null;
  showNewReferral = false;
  isDrafting = false;
  draftError: string | null = null;
  lastDraft: SummaryDraft | null = null;
  /** "Instruct MedicAI": how to change the current draft */
  instruction = '';
  readonly instructionMax = REFERRAL_SUMMARY_INSTRUCTION_MAX;
  /** the draft being streamed into the summary; pieces for any other stream are ignored */
  private streamId: string | null = null;
  expandedSummaryId: string | null = null;
  private shareBackApplied = false;
  private preselectApplied = false;
  /** links referred to from this dialog, for the result (a network chat marks its patient as referred) */
  private readonly sentLinkIds = new Set<string>();
  /** §18 V6: which recipient's not-shared entries are open, and what was loaded for each (on first open). */
  expandedUnsharedId: string | null = null;
  unshared: Record<string, UnsharedEntry[] | 'loading' | 'error'> = {};
  readonly unsharedLine = unsharedEntryLine;
  aiGate: SummaryAiGate = { locked: false, lowBalance: null };
  /** §16 M2: the files chosen to go with the summary, as the picker showed them. */
  get chosenFiles(): ShareableFile[] {
    return this._chosenFiles;
  }
  set chosenFiles(files: ShareableFile[]) {
    this._chosenFiles = files;
    // A stable array for app-file-gallery: rebuilt only when the choice changes, never per change detection.
    this.chosenGallery = shareableGalleryFiles(files);
  }
  private _chosenFiles: ShareableFile[] = [];
  chosenGallery: FileObject[] = [];
  readonly filesMax = REFERRAL_SUMMARY_FILES_MAX;
  readonly aiLockedMessage = AI_TOKENS_LOCKED_MESSAGE;
  private changed = false;

  form: NewReferralForm = this.emptyForm();

  readonly urgencies: ReferralUrgency[] = ['routine', 'soon', 'urgent'];
  readonly urgencyLabels = URGENCY_LABELS;
  readonly futureModes: ReferralFutureMode[] = ['none', 'until', 'forever'];
  readonly futureModeLabels = FUTURE_MODE_LABELS;
  readonly reasonMax = REFERRAL_REASON_MAX;
  readonly summaryMax = REFERRAL_SUMMARY_MAX;
  readonly draftNote = summaryDraftNote;
  readonly statusLabel = recipientStatusLabel;
  readonly shareSummary = recipientShareSummary;
  readonly minFutureDate = new Date(Date.now() + 24 * 60 * 60 * 1000);

  constructor(
    private referralService: ReferralService,
    private groupService: GroupService,
    private chat: ChatService,
    private tokenUsageService: TokenUsageService,
    private socketService: SocketService,
    private dialogRef: MatDialogRef<PatientReferralDialogComponent, PatientReferralDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: PatientReferralDialogData,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.load();
    this.chat.enabled$.pipe(takeUntil(this.destroy$)).subscribe((on) => (this.chatEnabled = on === true));
    void this.chat.ensureStarted();
    combineLatest([this.groupService.selectedGroup$, this.tokenUsageService.tokenBalance$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([group, balance]) => {
        this.aiGate = summaryAiGate(group?.isPremium === true, balance);
      });
    this.socketService.listen('aiOutputUpdate-referralSummary', 'patient-referral-dialog')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        const next = applySummaryStream(this.form.summaryText, event, this.streamId);
        if (!next) return;
        this.form = { ...this.form, summaryText: next.text };
        if (next.done) this.streamId = null;
      });
  }

  get problems(): string[] {
    const problems = newReferralProblems(this.form);
    // §25: the send would be refused. Said here rather than caught from the server, because the form can say who.
    if (this.form.linkIds.length && !this.allowedModes.includes(this.form.shareMode)) {
      problems.push('A clinician outside MedicAI receives a referral note. Send the patient file only to another MedicAI practice.');
    }
    return problems;
  }

  get pendingSelected(): ReferralRecipientOption[] {
    return this.options.filter((o) => o.status === 'pending' && this.form.linkIds.includes(o.linkId));
  }

  load(): void {
    this.isLoading = true;
    this.loadError = null;
    this.unshared = {};
    forkJoin({
      referrals: this.referralService.listPatientReferrals(this.data.patientId),
      recipients: this.referralService.listRecipients(),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ referrals, recipients }) => {
        this.referrals = referrals;
        this.options = recipients.options;
        this.switchedOffLinks = recipients.switchedOff;
        this.acknowledgement = recipients.acknowledgement;
        this.isLoading = false;
        // First referral for this patient: open straight onto the form.
        this.showNewReferral = this.data.canRefer && !referrals.length && !!this.options.length;
        const shareBack = this.data.shareBack;
        if (shareBack && this.data.canRefer && this.options.some((o) => o.linkId === shareBack.linkId) && !this.shareBackApplied) {
          this.shareBackApplied = true;
          this.showNewReferral = true;
          this.form = { ...this.form, linkIds: [shareBack.linkId], shareMode: shareBack.shareMode, futureMode: shareBack.shareMode === 'summary' ? 'none' : this.form.futureMode,
            reason: this.form.reason || `Sharing back on the referral from ${shareBack.practiceName}` };
        }
        const preselect = this.data.preselectLinkId;
        const option = preselect ? this.options.find((o) => o.linkId === preselect) : undefined;
        if (option && this.data.canRefer && !this.data.shareBack && !this.preselectApplied) {
          this.preselectApplied = true;
          this.showNewReferral = true;
          this.form = { ...this.form, linkIds: [] };
          this.toggleRecipient(option, true);
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.loadError = describeReferralFailure(error, "This patient's referrals could not be loaded.");
      },
    });
  }

  isSelected(option: ReferralRecipientOption): boolean {
    return this.form.linkIds.includes(option.linkId);
  }

  toggleRecipient(option: ReferralRecipientOption, checked: boolean): void {
    const without = this.form.linkIds.filter((id) => id !== option.linkId);
    this.form.linkIds = checked ? [...without, option.linkId] : without;
    // §25: who is being referred to decides what may be sent, so the choice is re-made every time the list changes.
    // Adding a clinician outside MedicAI to a full referral moves it to a referral note rather than failing on send.
    const allowed = this.allowedModes;
    if (!allowed.includes(this.form.shareMode)) {
      this.form = { ...this.form, shareMode: allowed[0] ?? 'summary', futureMode: 'none' };
    }
  }

  /** §25: the modes every chosen recipient accepts. One entry means there is nothing to ask. */
  get allowedModes(): ReferralShareMode[] {
    return allowedShareModes(this.options, this.form.linkIds).filter((m) => m !== 'items');
  }

  /** Every chosen recipient is capped at a referral note: say so once, instead of offering a choice of one. */
  get isLetterOnly(): boolean {
    return letterOnly(this.options, this.form.linkIds);
  }

  /** Generates a draft; a second draft replaces the text, so the physician confirms before losing their edits. */
  async generateDraft(): Promise<void> {
    if (this.isDrafting || this.aiGate.locked) return;
    if (this.form.summaryText.trim()) {
      const confirmed = await this.showConfirmationDialog(
        'REPLACE THE SUMMARY',
        'Generate a new draft? It replaces the text below, including your edits, and uses your practice\'s AI tokens again.',
        undefined, 'Cancel', 'Generate new draft', true);
      if (!confirmed) return;
    }
    this.requestDraft({});
  }

  /** "Instruct MedicAI": the current draft rewritten as the physician asks (Enter sends, Shift+Enter is a new line). */
  refineDraft(event: Event | null): void {
    if ((event as KeyboardEvent | null)?.shiftKey) return;
    event?.preventDefault();
    const instruction = this.instruction.trim();
    if (!instruction || this.isDrafting || this.aiGate.locked || !this.form.summaryText.trim()) return;
    this.instruction = '';
    this.requestDraft({ instruction, currentText: this.form.summaryText });
  }

  /**
   * Streams the draft into the summary as it is written; the answer then replaces it whole, so a lost socket piece never
   * leaves the text short. On failure the text from before is put back.
   */
  private requestDraft(refine: { instruction?: string; currentText?: string }): void {
    const before = this.form.summaryText;
    this.isDrafting = true;
    this.draftError = null;
    this.streamId = crypto.randomUUID();
    this.form = { ...this.form, summaryText: '' };
    this.referralService.createSummaryDraft(this.data.patientId, { reason: this.form.reason.trim(), linkIds: this.form.linkIds, streamId: this.streamId, ...refine })
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: (draft) => {
          this.isDrafting = false;
          this.streamId = null;
          this.lastDraft = draft;
          this.form = applySummaryDraft(this.form, draft);
        },
        error: (error) => {
          this.isDrafting = false;
          this.streamId = null;
          this.form = { ...this.form, summaryText: before };
          this.draftError = describeReferralFailure(error, 'The summary could not be generated. Try again, or write the summary yourself.');
        },
      });
  }

  /** Opens the picker over the patient's shareable files; the choice replaces the current one. */
  chooseFiles(): void {
    const data: ReferralSummaryFilePickerData = { patientId: this.data.patientId, selected: this.chosenFiles };
    this.dialog.open(ReferralSummaryFilePickerComponent, { data, width: '860px', maxWidth: '95vw' }).afterClosed()
      .pipe(takeUntil(this.destroy$)).subscribe((files: ShareableFile[] | undefined) => {
        if (!files) return;
        this.chosenFiles = files;
        this.form = { ...this.form, summaryFileRefs: files.map((f) => f.fileRef) };
      });
  }

  removeFile(file: ShareableFile): void {
    this.chosenFiles = this.chosenFiles.filter((f) => f.fileRef !== file.fileRef);
    this.form = { ...this.form, summaryFileRefs: this.chosenFiles.map((f) => f.fileRef) };
  }

  /** The gallery's delete (after its own confirmation) gives an index into chosenGallery, which mirrors chosenFiles. */
  removeGalleryFile(index: number): void {
    const file = this.chosenFiles[index];
    if (file) this.removeFile(file);
  }

  toggleUnshared(recipient: PatientReferralRecipient): void {
    if (this.expandedUnsharedId === recipient.id) {
      this.expandedUnsharedId = null;
      return;
    }
    this.expandedUnsharedId = recipient.id;
    const loaded = this.unshared[recipient.id];
    if (loaded && loaded !== 'error') return;
    this.unshared[recipient.id] = 'loading';
    this.referralService.listUnsharedEntries(recipient.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (entries) => { this.unshared[recipient.id] = entries; },
      error: () => { this.unshared[recipient.id] = 'error'; },
    });
  }

  unsharedList(recipientId: string): UnsharedEntry[] | null {
    const loaded = this.unshared[recipientId];
    return Array.isArray(loaded) ? loaded : null;
  }

  toggleSummary(referral: PatientReferral): void {
    this.expandedSummaryId = this.expandedSummaryId === referral.id ? null : referral.id;
  }

  canStop(recipient: PatientReferralRecipient): boolean {
    return this.data.canRefer && canStopSharing(recipient);
  }

  canWiden(referral: PatientReferral, recipient: PatientReferralRecipient): boolean {
    return this.data.canRefer && canWidenToFull(referral, recipient);
  }

  /**
   * §21.7: from chosen entries to the whole file. Asks the same two things a referral asks - how long, and the
   * acknowledgement - because that is what it becomes. One way: there is no button back.
   */
  async widen(referral: PatientReferral, recipient: PatientReferralRecipient): Promise<void> {
    if (!this.acknowledgement || this.wideningRecipientId) return;
    const confirmed = await this.showConfirmationDialog(
      'SHARE THE WHOLE FILE',
      `${recipient.displayName || 'This clinician'} has been sent entries you chose. Share ${this.data.patientName}'s whole file with them `
      + 'from now on, including everything already in it? This cannot be undone, and there is no way back to sharing only chosen entries.',
      undefined, 'Cancel', 'Share whole file');
    if (!confirmed) return;
    this.wideningRecipientId = recipient.id;
    this.referralService.widenReferral(recipient.id, 'forever', null, this.acknowledgement.version)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.wideningRecipientId = null;
          this.showToastMessage('They now receive the whole patient file. The rest of it reaches them shortly.');
          this.load();
        },
        error: (error) => {
          this.wideningRecipientId = null;
          this.showToastMessage(describeReferralFailure(error, 'This referral could not be widened. Please try again.'));
        },
      });
  }

  submit(): void {
    this.submitAttempted = true;
    if (this.problems.length || !this.acknowledgement || this.isSubmitting || this.isDrafting) return;
    this.isSubmitting = true;
    this.submitError = null;
    // A share-back goes only to the referring practice (the server refuses anything else).
    const replyTo = this.data.shareBack && this.form.linkIds.length === 1 && this.form.linkIds[0] === this.data.shareBack.linkId ? this.data.shareBack.replyToReceiptId : null;
    const linkIds = [...this.form.linkIds];
    this.referralService.createReferral(this.data.patientId, this.form, this.acknowledgement.version, replyTo)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: (result) => {
          this.isSubmitting = false;
          this.changed = true;
          for (const id of linkIds) this.sentLinkIds.add(id);
          const sent = result.awaitingLink
            ? `Referral created. ${result.awaitingLink} recipient(s) will receive it once they accept your invitation.`
            : 'Referral sent.';
          // Demographics longer than a referral carries were shortened rather than refused: said, so they can be corrected.
          const shortened = referralFieldNames(result.shortenedFields ?? []);
          this.showToastMessage(shortened ? `${sent} ${shortened} ${shortened.includes(' and ') ? 'were' : 'was'} too long and ${shortened.includes(' and ') ? 'were' : 'was'} shortened; please check the patient's details.` : sent);
          this.form = this.emptyForm();
          this.lastDraft = null;
          this.draftError = null;
          this.instruction = '';
          this.chosenFiles = [];
          this.submitAttempted = false;
          this.showNewReferral = false;
          // The chat for each recipient, opened empty so the practice can write first (§11). The SERVER opens these
          // too, after the commit, and that is the guarantee; this is what makes the room appear in the list at once
          // (it comes back as a summary, and joins the sender). Both are idempotent — the second call just finds it.
          // Deliberately NOT awaited and never surfaced: the referral has been sent, and chat being down is not that.
          for (const id of result.sentRecipientIds ?? []) void this.chat.ensureReferralChat(id).catch(() => undefined);
          this.load();
        },
        error: (error) => {
          this.isSubmitting = false;
          this.submitError = describeReferralFailure(error, 'The referral could not be sent. Please try again.');
        },
      });
  }

  /** The referral chat with this recipient (M6), in the floating chat window; this dialog closes. */
  async openChat(recipient: PatientReferralRecipient): Promise<void> {
    if (this.openingChatId) return;
    this.openingChatId = recipient.id;
    try {
      await this.chat.openReferralChat(recipient.id);
      this.dialogRef.close();
    } catch {
      this.showToastMessage('The referral chat could not be opened. Please try again.');
    } finally {
      this.openingChatId = null;
    }
  }

  async stopSharing(recipient: PatientReferralRecipient): Promise<void> {
    const confirmed = await this.showConfirmationDialog(
      'STOP SHARING',
      `Stop sharing new entries with ${recipient.displayName || 'this recipient'}? Records already shared stay with them.`,
      undefined, 'Cancel', 'Stop sharing');
    if (!confirmed) return;
    this.stoppingRecipientId = recipient.id;
    this.referralService.stopSharing(recipient.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.stoppingRecipientId = null;
        this.changed = true;
        this.load();
      },
      error: (error) => {
        this.stoppingRecipientId = null;
        this.showToastMessage(describeReferralFailure(error, 'Sharing could not be stopped. Please try again.'));
      },
    });
  }

  close(): void {
    this.dialogRef.close({ changed: this.changed, sentLinkIds: [...this.sentLinkIds] });
  }

  trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  private emptyForm(): NewReferralForm {
    // D11: future sharing has no default; the user chooses every time.
    return {
      linkIds: [], reason: '', urgency: 'routine', futureMode: null, futureUntil: null, acknowledged: false,
      shareMode: 'full', summaryText: '', summaryAiAssisted: false, summaryFileRefs: [],
    };
  }
}
